import { useState } from "react"

import type { ActionsDelayData, UIEvent } from "../types"
import { CardActions, CardButton, CardShell } from "./card_shell"
import { SelectInput, type SelectInputOption } from "./select_input"
import styles from "./actions_delay_card.module.scss"

interface Props {
  ui: UIEvent
  onRespond: (uiId: string, actionId: string, value: unknown, displayText?: string) => void
}

const HOURS = ["00", "01", "02", "03"]
const MINUTES = ["00", "01", "02", "03", "04", "05", "10", "15", "20", "25", "30", "35", "40", "45", "50", "55"]
const SECONDS = ["00", "15", "30", "45"]
const MAX_DELAY_SECONDS = 10800

function pad(value: number): string {
  return String(value).padStart(2, "0")
}

function withValue(list: string[], value: string): SelectInputOption[] {
  const values = list.includes(value) ? list : [...list, value].sort((a, b) => Number(a) - Number(b))
  return values.map(option => ({ value: option, label: option }))
}

const ActionsDelayCard = ({ ui, onRespond }: Props) => {
  const data = ui.data as ActionsDelayData
  const initial = Math.min(Math.max(data.current ?? data.default ?? 0, 0), MAX_DELAY_SECONDS)

  const [answered, setAnswered] = useState(false)
  const [hours, setHours] = useState(pad(Math.floor(initial / 3600)))
  const [minutes, setMinutes] = useState(pad(Math.floor((initial % 3600) / 60)))
  const [seconds, setSeconds] = useState(pad(initial % 60))

  const maxedOut = hours === "03"

  const changeHours = (value: string) => {
    setHours(value)
    if (value === "03") {
      setMinutes("00")
      setSeconds("00")
    }
  }

  const submit = () => {
    if (answered) return
    setAnswered(true)
    const total = Number(hours) * 3600 + Number(minutes) * 60 + Number(seconds)
    const displayText = total === 0 ? "No delay" : `Delay actions by ${hours}:${minutes}:${seconds}`
    onRespond(ui.id, "select", total, displayText)
  }

  return (
    <CardShell prompt={data.prompt} onDismiss={() => onRespond(ui.id, "close", undefined)}>
      <div className={styles["controls"]}>
        <label className={styles["field"]}>
          <span className={styles["label"]}>Hours</span>
          <SelectInput
            ariaLabel="Hours"
            value={hours}
            options={withValue(HOURS, hours)}
            onChange={changeHours}
            disabled={answered}
          />
        </label>
        <span className={styles["separatorField"]} aria-hidden="true">
          <span className={styles["label"]}>&nbsp;</span>
          <span className={styles["separator"]}>:</span>
        </span>
        <label className={styles["field"]}>
          <span className={styles["label"]}>Minutes</span>
          <SelectInput
            ariaLabel="Minutes"
            value={minutes}
            options={withValue(MINUTES, minutes)}
            onChange={setMinutes}
            disabled={answered || maxedOut}
          />
        </label>
        <span className={styles["separatorField"]} aria-hidden="true">
          <span className={styles["label"]}>&nbsp;</span>
          <span className={styles["separator"]}>:</span>
        </span>
        <label className={styles["field"]}>
          <span className={styles["label"]}>Seconds</span>
          <SelectInput
            ariaLabel="Seconds"
            value={seconds}
            options={withValue(SECONDS, seconds)}
            onChange={setSeconds}
            disabled={answered || maxedOut}
          />
        </label>
      </div>
      <CardActions>
        <CardButton variant="primary" disabled={answered} onClick={submit}>
          Select
        </CardButton>
      </CardActions>
    </CardShell>
  )
}

export default ActionsDelayCard
